<template>
  <div>
    <img class="upload__img" :class="this.disabled ? '' : 'active'" :src="value ? `data:image/jpg;base64,${value}` : defaultImg" @click="toggleOpenFileUpload">
    <ScrollableDialog dialogTitle="上傳照片 / Upload photo" v-model="handleOpen" :width="!$vuetify.breakpoint.mdAndDown ? '800px' : '375px'">
      <v-card-text class="d-flex flex-column">
        <div class="red--text">請上傳三個月內拍攝之證件（大頭）照電子檔，影像解析度需高於300像素，檔案大小不得大於1MB之JPEG檔案<br />
          Please upload a photo taken in three months. The image resolution must be higher than 300pixels, and the file size should not exceed 1MB.</div>
        <div class="my-2">
          <DefaultButton :colorType="'primary'" class="mr-2 mb-2 text__size-14" height="38" @doFunction="toggleFileUpload">
            <v-icon class="cus-Btn__primary__addIconHover">mdi-plus</v-icon>
            <div class="btn-group__text"><span>選擇檔案</span><span>Select file</span></div>
          </DefaultButton>
          <input type="file" ref="uploader" class="d-none" @change="onFileChanged" />
        </div>
        <div class="d-flex flex-column" v-if="selectedFile">
          <div class="upload__cropper">
            <div class="cropper__box">
              <VueCropper
                ref="cropper"
                class="cropper__img"
                :src="img"
                :stencil-props="{
                  handlers: {},
                  movable: false,
                  scalable: false,
                  aspectRatio: 7/9,
                }"
                :stencil-size="{
                  width: !$vuetify.breakpoint.mdAndDown ? 315 : 245,
                  height: !$vuetify.breakpoint.mdAndDown ? 405 : 315
                }"
                image-restriction="stencil"
                @change="onChange"
                :default-size="defaultSize"
              ></VueCropper>
              <v-slider
                class="cropper__zoom"
                :value="zoom"
                @change="onZoom"
                :debounce="false"
              ></v-slider>
            </div>
            <div class="preview__box">
              <VuePreview
                class="preview__img"
                :image="result.image"
                :coordinates="result.coordinates"
              ></VuePreview>
              <span>預覽/Preview</span>
            </div>
          </div>
          <FormFooter v-if="selectedFile">
            <DefaultButton class="text__size-14" @doFunction="toggleFileCreate" height="auto">
              <div class="btn-group__text">
                <span>上傳</span>
                <span>Upload</span>
              </div>
            </DefaultButton>
            <DefaultButton class="text__size-14" @doFunction="handleOpen = false" height="auto">
              <div class="btn-group__text">
                <span>取消</span>
                <span>Close</span>
              </div>
            </DefaultButton>
          </FormFooter>
        </div>
      </v-card-text>
    </ScrollableDialog>
  </div>
</template>

<script>

export default {
  props: {
    value: {
      default: null,
      require: true,
    },
    disabled: {
      default: false,
    },
  },
  data() {
    return {
      img: null,
      result: {
        coordinates: null,
        image: null,
      },
      handleOpen: false,
      selectedFile: null,
      zoom: 0,
      defaultImg: 'data:image/svg+xml,%3Csvg xmlns="http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg" width="1em" height="1em" preserveAspectRatio="xMidYMid meet" viewBox="0 0 24 24"%3E%3Cpath fill="white" d="M16 8c0 2.21-1.79 4-4 4s-4-1.79-4-4l.11-.94L5 5.5L12 2l7 3.5v5h-1V6l-2.11 1.06L16 8m-4 6c4.42 0 8 1.79 8 4v2H4v-2c0-2.21 3.58-4 8-4Z"%2F%3E%3C%2Fsvg%3E',
    };
  },
  watch: {
    handleOpen(val) {
      if (val === false) {
        this.handleUploaderClear();
      }
    },
  },
  methods: {
    toggleFileUpload() {
      this.$refs.uploader.click();
    },
    async toggleOpenFileUpload() {
      if (this.disabled) {
        return;
      }

      if (this.value) {
        const base64Response = await fetch(`data:image/jpeg;base64,${this.value}`);
        const blob = await base64Response.blob();

        this.selectedFile = new File([blob], 'image.jpg', { type: blob.type });
        this.img = window.URL.createObjectURL(blob);
      }

      this.handleOpen = true;
    },
    async toggleFileCreate() {
      const { canvas } = this.$refs.cropper.getResult();

      const finalCanvas = this.resizeIfTooWide(canvas, 4096); // ⬅️ 最大寬度限制

      const maxSizeMB = this.selectedFile.size / (1024 * 1024); // bytes轉MB
      const base64 = await this.compressCanvasToBase64(finalCanvas, maxSizeMB); // ⬅️ 限制大小

      this.$emit('input', base64);
      this.handleOpen = false;
    },
    resizeIfTooWide(canvas, maxWidth = 4096) {
      if (canvas.width <= maxWidth) return canvas;

      const scale = maxWidth / canvas.width;
      const resizedCanvas = document.createElement('canvas');
      resizedCanvas.width = maxWidth;
      resizedCanvas.height = canvas.height * scale;

      const ctx = resizedCanvas.getContext('2d');
      ctx.drawImage(canvas, 0, 0, resizedCanvas.width, resizedCanvas.height);

      return resizedCanvas;
    },
    compressCanvasToBase64(canvas, maxSizeMB = 1) {
      return new Promise((resolve) => {
        let quality = 0.9;

        const tryCompress = () => {
          canvas.toBlob((blob) => {
            if (blob.size <= maxSizeMB * 1024 * 1024 || quality <= 0.3) {
              const reader = new FileReader();
              reader.onloadend = () => {
                const base64 = reader.result
                  .replace('data:', '')
                  .replace(/^.+,/, '');
                resolve(base64);
              };
              reader.readAsDataURL(blob);
            } else {
              quality -= 0.05;
              tryCompress();
            }
          }, 'image/jpeg', quality);
        };

        tryCompress();
      });
    },
    onFileChanged(e) {
      if (e.target.files[0] === undefined) return;

      const file = e.target.files[0];
      const { type, size } = file;

      if (type.match('image/jpeg')) {
        if (size <= 1 * 1024 * 1024) {
          this.createImage(file);
        } else {
          this.$swal.fire({
            icon: 'error',
            title: '上傳大小不可大於1MB！',
          });
          this.handleUploaderClear();
        }
      } else {
        this.$swal.fire({
          icon: 'error',
          title: '上傳格式錯誤！',
        });
        this.handleUploaderClear();
      }
    },
    handleUploaderClear() {
      this.$refs.uploader.value = null;
      this.selectedFile = null;
      this.img = null;
      this.result = {
        coordinates: null,
        image: null,
      };
    },
    createImage(file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        let data;
        if (typeof e.target.result === 'object') {
          // 把Array Buffer转化为blob 如果是base64不需要
          data = window.URL.createObjectURL(new Blob([e.target.result]));
        } else {
          data = e.target.result;
        }
        this.img = data;
      };
      this.selectedFile = file;
      reader.readAsArrayBuffer(file);
    },
    onChange() {
      const { cropper } = this.$refs;
      if (cropper) {
        const { coordinates, imageSize, image } = cropper;

        if (
          imageSize.width / imageSize.height
          > coordinates.width / coordinates.height
        ) {
          // Determine the position of slider bullet
          // It's 0 if the stencil has the maximum size and it's 1 if the has the minimum size
          this.zoom = ((cropper.imageSize.height - cropper.coordinates.height)
            / (cropper.imageSize.height - cropper.sizeRestrictions.minHeight)) * 100;
        } else {
          this.zoom = ((cropper.imageSize.width - cropper.coordinates.width)
            / (cropper.imageSize.width - cropper.sizeRestrictions.minWidth)) * 100;
        }

        this.result = {
          coordinates,
          image,
        };
      }
    },
    onZoom(value) {
      const { cropper } = this.$refs;
      if (cropper) {
        if (cropper.imageSize.height < cropper.imageSize.width) {
          const { minHeight } = cropper.sizeRestrictions;
          const imageHeight = cropper.imageSize.height;
          // Determine the current absolute zoom and the new absolute zoom
          // to calculate the sought relative zoom value
          cropper.zoom(
            (imageHeight - (this.zoom / 100) * (imageHeight - minHeight))
              / (imageHeight - (value / 100) * (imageHeight - minHeight)),
          );
        } else {
          const { minWidth } = cropper.sizeRestrictions;
          const imageWidth = cropper.imageSize.width;
          cropper.zoom(
            (imageWidth - (this.zoom / 100) * (imageWidth - minWidth))
              / (imageWidth - (value / 100) * (imageWidth - minWidth)),
          );
        }
      }
    },
    defaultSize({ imageSize, visibleArea }) {
      return {
        width: (visibleArea || imageSize).width,
        height: (visibleArea || imageSize).height,
      };
    },
  },
};
</script>

<style lang="scss" scoped>

.upload {

  &__cropper {
    @include pad-width() {
      display: grid;
      grid-template-columns: 1fr 200px;
    }
  }

  &__img {
    width: 175px;
    height: 225px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    background-color: #DDD;

  }
  &__img.active {
    cursor: pointer;
  }
}

.cropper {

  &__box {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: auto;
  }

  &__img {
    width: 270px;
    height: 350px;
    background: #DDD;
    cursor: move;

    @include pad-width() {
      width: 350px;
      height: 450px;
    }
  }

  &__zoom {
    width: 100%;
  }
}

.preview {

  &__box {
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &__img {
    width: 175px;
    height: 225px;
    overflow: hidden;
    border: 1px solid rgba(0, 0, 0, 0.15);
    background-color: #fff;
    margin: 1rem;
  }

}

</style>
